'use client';

import { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { IEnhancedImage, IFooter } from '@/.generated';
import { buttonVariants } from '@/helpers/Wrappers/ButtonWrapper/ButtonWrapper.styles';
import ImageWrapper from '@/helpers/Wrappers/ImageWrapper/ImageWrapper';
import ModalWrapper from '@/helpers/Wrappers/ModalWrapper/ModalWrapper';
import PlainTextWrapper from '@/helpers/Wrappers/PlainTextWrapper/PlainTextWrapper';
import RichTextWrapper from '@/helpers/Wrappers/RichTextWrapper/RichTextWrapper';
import { getCSLPAttributes } from '@/utils/type-guards';
import { trackAdobeClick } from '@/utils/adobe/click';
import { MY_ACCOUNT_LOGIN_URL } from '@/utils/Constants';
import { stickyTileModalVariants } from './StickyTiles.styles';

type LogInTileData = NonNullable<NonNullable<IFooter['sticky_tiles_section']>['log_in_tile']>;

const {
  tileWrapper,
  tileButton,
  tileLabel,
  modalSubmit,
  modalBody,
  modalTitle,
  modalForm,
  modalInput,
  modalActions,
  modalForgotLink,
  modalDisclaimer,
  modalSignUp,
} = stickyTileModalVariants();


export const StickyTileModal = ({ tile }: { tile?: LogInTileData }) => {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [portalContainer, setPortalContainer] = useState<Element | null>(null);

  // The <srp-footer> web component scopes all its Tailwind classes to descendants of that
  // custom element; portal the modal there instead of document.body so it keeps its styles.
  // The regular Next.js page has no <srp-footer> ancestor, so this falls back to document.body.
  useEffect(() => {
    setPortalContainer(wrapperRef.current?.closest('srp-footer') ?? document.body);
  }, []);

  if (!tile?.icon && !tile?.title) return null;

  const modal = tile.account_login_modal;
  const accountLogin = modal?.my_account_login_reference?.[0];

  const loginSubmitClass = buttonVariants({
    variant: accountLogin?.login_button?.cta_variant ?? 'primary',
    size: 'variable',
    className: modalSubmit(),
  });

  return (
    <>
      <div
        ref={wrapperRef}
        className={tileWrapper()}
        style={{
          backgroundColor: tile.tile_background_color?.color_list ?? undefined,
          color: tile.tile_text_color?.color_list ?? undefined,
        }}
      >
        <button
          type="button"
          className={tileButton()}
          onClick={() => {
            trackAdobeClick({
              clickName: tile.title ?? 'Sticky Tile',
              clickType: 'button',
              clickLocation: 'stickyTile',
              clickUrl: MY_ACCOUNT_LOGIN_URL,
            });
            setIsOpen(true);
          }}
        >
          <ImageWrapper
            image={
              {
                image: tile.icon,
                alternate_text: tile.title,
                $: { image: tile.$?.icon },
              } as IEnhancedImage
            }
            width="50%"
            height="auto"
          />
          <PlainTextWrapper
            content={tile.title}
            className={tileLabel()}
            cslpAttribute={tile.$?.title}
          />
        </button>
      </div>

      <ModalWrapper
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title={modal?.modal_title ?? tile.title ?? ''}
        container={portalContainer}
      >
        {modal && (
          <div className={modalBody()}>
            <PlainTextWrapper
              content={modal.modal_title}
              className={modalTitle()}
              cslpAttribute={modal.$?.modal_title}
            />
            <form
              id="loginForm"
              method="post"
              action={accountLogin?.login_button?.login_link?.href}
              className={modalForm()}
              onSubmit={() =>
                trackAdobeClick({
                  clickName: accountLogin?.login_button?.login_link?.title ?? 'Login',
                  clickType: 'button',
                  clickLocation: 'stickyTileLogin',
                  clickUrl: MY_ACCOUNT_LOGIN_URL,
                })
              }
            >
              <input
                type="text"
                id="UserName"
                name="UserName"
                autoComplete="username"
                placeholder={accountLogin?.username_field_placeholder}
                className={modalInput()}
                {...getCSLPAttributes(accountLogin?.$?.username_field_placeholder)}
              />
              <input
                type="password"
                id="Password"
                name="Password"
                autoComplete="current-password"
                placeholder={accountLogin?.password_field_placeholder}
                className={modalInput()}
                {...getCSLPAttributes(accountLogin?.$?.password_field_placeholder)}
              />
              <div className={modalActions()}>
                {accountLogin?.forgot_password_link?.href && (
                  <Link
                    href={accountLogin?.forgot_password_link.href}
                    className={modalForgotLink()}
                    onClick={() =>
                      trackAdobeClick({
                        clickName: accountLogin?.forgot_password_link?.title ?? 'Forgot Password',
                        clickType: 'link',
                        clickLocation: 'stickyTileLogin',
                      })
                    }
                    {...getCSLPAttributes(accountLogin?.$?.forgot_password_link)}
                  >
                    <PlainTextWrapper content={accountLogin?.forgot_password_link.title} />
                  </Link>
                )}
                <input
                  type="submit"
                  id="loginSubmit"
                  value={accountLogin?.login_button?.login_link?.title}
                  className={loginSubmitClass}
                  {...getCSLPAttributes(accountLogin?.$?.login_button)}
                />
              </div>
            </form>
            <RichTextWrapper
              content={accountLogin?.disclaimer_note}
              className={modalDisclaimer()}
              cslpAttribute={accountLogin?.$?.disclaimer_note}
            />
            <RichTextWrapper
              content={accountLogin?.sign_up_content}
              className={modalSignUp()}
              cslpAttribute={accountLogin?.$?.sign_up_content}
            />
          </div>
        )}
      </ModalWrapper>
    </>
  );
};
